import React, { useEffect } from "react";
import { Document, Image, Page, Text, View } from "@react-pdf/renderer";
import { INewOrderDetails } from "../../pages/newOrder/useNewOrderDetails";
import { purchaseSummaryStyles } from "../../styles/PurchaseSummaryStyles";
import { NumberToCurrency, ShowAsPhoneNumber } from "../../utils/Formatters";
import { GetDiscountPrice } from "../../utils/ProductUtils";
import { termsAndConditions } from "../../legal/TermsAndConditions";
import { Business } from "api/entities/Business";

export interface IPurchaseSummary {
  business: Business;
  order: INewOrderDetails;
  signatureUrl?: string;
  onTextChange?: (text: string) => void;
}

export const PurchaseSummary = ({
  business,
  order,
  signatureUrl,
  onTextChange,
}: IPurchaseSummary) => {
  const styles = purchaseSummaryStyles;

  useEffect(() => {
    onTextChange?.(
      termsAndConditions
        .replace("{CustomerName}", business.name)
        .replace(
          "{ContractLength}",
          order.productDetails?.contractLengthMonths.toString() ?? "",
        ),
    );
  }, [business, order]);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.titleSection}>
          <Text style={[styles.titleHeading, styles.centered]}>
            Decibel 130 Advertising Agreement
          </Text>
        </View>
        {business && (
          <View style={styles.section}>
            <Text style={styles.heading}>Business Information</Text>
            <Text style={styles.text}>{business.name}</Text>
            <Text style={styles.text}>
              {business.clientFirstName} {business.clientLastName}
            </Text>
            <Text style={styles.text}>{business.address.street}</Text>
            <Text style={styles.text}>
              {business.address.city}, {business.address.state}{" "}
              {business.address.zip}
            </Text>
            <Text style={styles.text}>{ShowAsPhoneNumber(business.phone)}</Text>
            <Text style={styles.text}>{business.email}</Text>
          </View>
        )}
        {order.productDetails && (
          <View style={styles.section}>
            <Text style={styles.heading}>{order.productDetails.name}</Text>
            <Text style={styles.description}>
              {order.productDetails.description}
            </Text>
            <Text style={styles.text}>
              Contract Length: {order.productDetails.contractLengthMonths}{" "}
              months
            </Text>
            <Text style={styles.text}>
              {order.discount && order.discount > 0 ? "Original " : ""}Price per
              Month: {NumberToCurrency(order.productDetails.originalPricePerMonth ?? order.productDetails.pricePerMonth)}
            </Text>
            {order.discount && order.discount > 0 && (
              <>
                <Text style={styles.text}>
                  Discount: {order.discount * 100}%
                </Text>
                <Text style={styles.text}>
                  Updated Price per Month{" "}
                  {NumberToCurrency(
                    GetDiscountPrice(
                      order.productDetails.originalPricePerMonth ?? order.productDetails.pricePerMonth,
                      order.discount,
                    ),
                  )}
                </Text>
              </>
            )}
            {order.productDetails.setupFee &&
              order.productDetails.setupFee > 0 && (
                <Text style={styles.text}>
                  Setup Fee: {NumberToCurrency(order.isOverrideSetupFee ? order.overrideSetupFee : order.productDetails.setupFee)}
                </Text>
              )}
            {order.productGroup?.clientFacingProductGroupServices &&
              order.productGroup.clientFacingProductGroupServices.length >
                0 && (
                <>
                  <Text style={styles.description}>Features:</Text>
                  <View style={styles.list}>
                    {order.productGroup?.clientFacingProductGroupServices &&
                      order.productGroup?.clientFacingProductGroupServices.map(
                        (feature, index) => (
                          <Text key={index} style={styles.bullet}>
                            • {feature}
                          </Text>
                        ),
                      )}
                  </View>
                </>
              )}
          </View>
        )}
        {order.addonLedger?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.heading}>Addons</Text>
            {order.addonLedger.map((item, index) => (
              <View key={index} style={styles.list}>
                <Text style={styles.description}>{item.name}</Text>

                {item.startDate && (
                  <>
                    <Text style={styles.bullet}>
                      • Start Date: {item.startDate}
                    </Text>
                  </>
                )}
                {item.contractLength && (
                  <>
                    <Text style={styles.bullet}>
                      • Contract Length: {item.contractLength} month
                      {item.contractLength > 1 ? "s" : ""}
                    </Text>
                  </>
                )}
                {item.pricePerUnit === 0 && item.recurringPrice === 0 && (
                  <>
                    <Text style={styles.bullet}>• Price: Free</Text>
                  </>
                )}
                {item.pricePerUnit > 0 && (
                  <>
                    <Text style={styles.bullet}>
                      • Price: {NumberToCurrency(item.pricePerUnit)}
                    </Text>
                  </>
                )}
                {item.recurringPrice > 0 && (
                  <>
                    <Text style={styles.bullet}>
                      • Recurring Price: {NumberToCurrency(item.recurringPrice)}
                    </Text>
                  </>
                )}
              </View>
            ))}
          </View>
        )}
        {order.productDetails && (
          <View style={styles.section}>
            <Text style={styles.footer}>
              {termsAndConditions
                .replace("{CustomerName}", business.name)
                .replace(
                  "{ContractLength}",
                  order.productDetails.contractLengthMonths.toString(),
                )}
            </Text>
            <Text style={{ ...styles.heading, marginTop: 20 }}>Signature</Text>
            {signatureUrl && (
              <Image style={styles.footerImage} src={signatureUrl} />
            )}
          </View>
        )}
      </Page>
    </Document>
  );
};
